import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_HIGH,
  $getSelection,
  $isRangeSelection,
} from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';

/**
 * Allowed HTML elements that map to Telegram formatting
 */
const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'INS', 'S', 'STRIKE', 'DEL',
  'CODE', 'PRE', 'A', 'BLOCKQUOTE', 'TG-SPOILER', 'TG-EMOJI',
  'SPAN', 'P', 'BR', 'DIV',
]);

/**
 * Sanitizes pasted HTML to only include Telegram-compatible elements.
 * Exported so the TGPE clipboard paste flow can reuse it for the
 * non-TGPE fallback branch.
 */
export function sanitizePastedTelegramHTML(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  function processNode(node: Node): void {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toUpperCase();
      
      // Convert to Telegram-compatible equivalents
      if (tagName === 'STRONG') {
        const b = doc.createElement('b');
        while (element.firstChild) {
          b.appendChild(element.firstChild);
        }
        element.replaceWith(b);
        processNode(b);
        return;
      }
      if (tagName === 'EM') {
        const i = doc.createElement('i');
        while (element.firstChild) {
          i.appendChild(element.firstChild);
        }
        element.replaceWith(i);
        processNode(i);
        return;
      }
      if (tagName === 'DEL' || tagName === 'STRIKE') {
        const s = doc.createElement('s');
        while (element.firstChild) {
          s.appendChild(element.firstChild);
        }
        element.replaceWith(s);
        processNode(s);
        return;
      }
      if (tagName === 'INS') {
        const u = doc.createElement('u');
        while (element.firstChild) {
          u.appendChild(element.firstChild);
        }
        element.replaceWith(u);
        processNode(u);
        return;
      }
      
      // Check if it's a spoiler span
      if (tagName === 'SPAN' && (
        element.classList.contains('tg-spoiler') ||
        element.classList.contains('spoiler')
      )) {
        const spoiler = doc.createElement('tg-spoiler');
        while (element.firstChild) {
          spoiler.appendChild(element.firstChild);
        }
        element.replaceWith(spoiler);
        processNode(spoiler);
        return;
      }
      
      // For non-allowed elements, unwrap (keep children)
      if (!ALLOWED_TAGS.has(tagName)) {
        const parent = element.parentNode;
        if (parent) {
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        }
        return;
      }
      
      // Remove all attributes except href for links and known attributes
      const attrs = Array.from(element.attributes);
      for (const attr of attrs) {
        if (tagName === 'A' && attr.name === 'href') continue;
        if (tagName === 'BLOCKQUOTE' && attr.name === 'expandable') continue;
        if (tagName === 'TG-EMOJI' && attr.name === 'emoji-id') continue;
        element.removeAttribute(attr.name);
      }
      
      // Process children
      const children = Array.from(element.childNodes);
      for (const child of children) {
        processNode(child);
      }
    }
  }
  
  // Process all children of body
  const children = Array.from(doc.body.childNodes);
  for (const child of children) {
    processNode(child);
  }
  
  return doc.body.innerHTML;
}

/**
 * PasteFilterPlugin strips non-Telegram HTML elements from pasted content
 * while preserving Telegram-compatible formatting.
 */
export function PasteFilterPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        // If the clipboard came from another Lexical editor (same app, another
        // window), let Lexical's built-in PASTE_COMMAND handler at
        // COMMAND_PRIORITY_EDITOR take it — that path rehydrates the full
        // editor state including paragraph breaks. Sanitizing the text/html
        // flavor instead would round-trip through DOM and lose structure.
        if (event.clipboardData?.types.includes('application/x-lexical-editor')) {
          return false;
        }

        const html = event.clipboardData?.getData('text/html');
        if (!html) return false; // Fall through to default text paste

        const sanitized = sanitizePastedTelegramHTML(html);
        const parser = new DOMParser();
        const dom = parser.parseFromString(sanitized, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);

        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes(nodes);
        }

        event.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}
